import * as React from "react"
import { useState, useCallback, useEffect } from "react"
import { DrawerSeeker } from "@ui-seeker/core"
import { Grid, TextField, Typography, ToggleButton, ToggleButtonGroup, Box, FormControlLabel, Switch, MenuItem, Accordion, AccordionSummary, AccordionDetails, Tooltip, InputAdornment } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

import { isEditScheduleDrawerOpen, selectedScheduleForEdit, scheduleFormData, resetScheduleFormData } from "../@signals"
import { createBatchFormData } from "../../Batches/@signals"
import { useScheduleMutations } from "../@hooks"
import ScheduleExecutionFields from "../ScheduleExecutionFields"
import { SitesAutocomplete, BrandsAutocomplete, CustomerAutocomplete } from "../../Batches/CreateBatchDrawer/@components"
import { buildSchedulePayload, isScheduleFormValid, BATCH_TYPE_OPTIONS, FREQUENCY_OPTIONS, DATE_RANGE_OPTIONS, CUSTOMER_REQUIRED_TYPES } from "../scheduleForm"


export default function EditScheduleDrawer() {
    const { updateSchedule, isSubmitting } = useScheduleMutations()
    const [isValid, setIsValid] = useState(false)

    // Load schedule data when drawer opens
    useEffect(() => {
        if (isEditScheduleDrawerOpen.value && selectedScheduleForEdit.value) {
            const schedule = selectedScheduleForEdit.value
            const config = schedule.config || {}

            const asp = config.autoSecondPass

            scheduleFormData.value = {
                schedule_name: schedule.scheduleName || '',
                batch_type: schedule.batchType || 'raw2sku',
                id_customer: schedule.idCustomer ?? null,
                frequency: schedule.frequency || 'monthly',
                every_n_days: schedule.everyNDays ?? null,
                // Empty when the schedule has no hour of its own: the field's helper text
                // names the default, so pre-filling it would silently pin the default as
                // an explicit value on the next save.
                execution_time: schedule.executionTime || '',
                name_template: schedule.nameTemplate || '',
                is_active: schedule.isActive ?? true,
                // Split the office-time string, do NOT go through `new Date()`: the raw
                // nextExecution is in the server clock and reparsing it as browser-local
                // moved the day for any hour near midnight.
                next_execution: (schedule.nextExecutionLocal || schedule.nextExecution || '').split('T')[0] || null,
                config: {
                    ...config,
                    maxDaysSinceSeen: config.maxDaysSinceSeen ?? null,
                    // Stored shape is the wire one; the form keeps its own controls.
                    autoSecondPass: asp ? {
                        enabled: true,
                        speedMode: asp.execution_mode === 'sync',
                        samplePct: Math.round((asp.sample_rate ?? 0) * 100)
                    } : null,
                    autoValidation: config.autoValidation || {
                        minRateAutovalidate: null,
                        minGapSecondCandidate: null,
                        maxRateAutoexclude: null
                    }
                }
            }

            // Sync createBatchFormData for autocompletes
            createBatchFormData.value = {
                ...createBatchFormData.value,
                batchType: schedule.batchType || 'raw2sku',
                customerID: schedule.idCustomer ?? null,
                sites: config.sites || [],
                brands: config.brands || [],
                dateStart: config.dateStart || null,
                dateEnd: config.dateEnd || null,
                dateField: config.dateField || 'end_date'
            }
        }
    }, [isEditScheduleDrawerOpen.value, selectedScheduleForEdit.value])

    // Validate form
    useEffect(() => {
        setIsValid(isScheduleFormValid(scheduleFormData.value, createBatchFormData.value))
    }, [scheduleFormData.value, createBatchFormData.value])

    const handleClose = useCallback(() => {
        if (!isSubmitting) {
            isEditScheduleDrawerOpen.value = false
            selectedScheduleForEdit.value = null
            resetScheduleFormData()
        }
    }, [isSubmitting])

    const handleUpdate = useCallback(async () => {
        const data = scheduleFormData.value
        const scheduleId = selectedScheduleForEdit.value?.idSchedule

        if (!scheduleId) return

        // The DAY only — the back anchors it at execution_time (see the create drawer).
        const updateData = {
            ...buildSchedulePayload(data, createBatchFormData.value),
            next_execution: data.next_execution || null
        }

        try {
            await updateSchedule(scheduleId, updateData)
            handleClose()
        } catch (err) {
            // Error handled by hook
        }
    }, [updateSchedule, handleClose])

    const batchType = scheduleFormData.value.batch_type

    if (!selectedScheduleForEdit.value) return null

    return (
        <DrawerSeeker
            title="Edit Batch Schedule"
            isOpen={isEditScheduleDrawerOpen.value}
            controller={handleClose}
            successProps={{
                onClick: handleUpdate,
                disabled: !isValid || isSubmitting,
                children: isSubmitting ? "Updating..." : "Update Schedule"
            }}
            cancelProps={{
                disabled: isSubmitting
            }}
            width="50%"
        >
            <Grid container spacing={3}>
                {/* Schedule Name */}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        required
                        label="Schedule Name"
                        value={scheduleFormData.value.schedule_name}
                        onChange={e => scheduleFormData.value = {
                            ...scheduleFormData.value,
                            schedule_name: e.target.value
                        }}
                        disabled={isSubmitting}
                    />
                </Grid>

                {/* Batch Type */}
                <Grid item xs={12}>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Batch Type
                        </Typography>
                        <ToggleButtonGroup
                            value={batchType}
                            exclusive
                            onChange={(e, newValue) => {
                                if (newValue !== null) {
                                    scheduleFormData.value = {
                                        ...scheduleFormData.value,
                                        batch_type: newValue
                                    }
                                }
                            }}
                            disabled={isSubmitting}
                            fullWidth
                            size="small"
                        >
                            {BATCH_TYPE_OPTIONS.map(opt => (
                                <ToggleButton key={opt.value} value={opt.value}>
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                </Grid>

                {/* Customer — required for the directions that are scoped by one */}
                {CUSTOMER_REQUIRED_TYPES.includes(batchType) && (
                    <Grid item xs={12}>
                        <CustomerAutocomplete
                            disabled={isSubmitting}
                            clientType={batchType === 'raw2raw' ? 'retailer' : undefined}
                        />
                    </Grid>
                )}

                {/* Frequency */}
                <Grid item xs={scheduleFormData.value.frequency === 'custom' ? 8 : 12}>
                    <TextField
                        select
                        fullWidth
                        required
                        label="Frequency"
                        helperText="Batches will be named: [Schedule Name] - YYYY-MM-DD"
                        value={scheduleFormData.value.frequency}
                        onChange={e => scheduleFormData.value = {
                            ...scheduleFormData.value,
                            frequency: e.target.value
                        }}
                        disabled={isSubmitting}
                    >
                        {FREQUENCY_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {scheduleFormData.value.frequency === 'custom' && (
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            label="Every N days"
                            value={scheduleFormData.value.every_n_days ?? ''}
                            onChange={e => scheduleFormData.value = {
                                ...scheduleFormData.value,
                                every_n_days: e.target.value === '' ? null : e.target.value
                            }}
                            disabled={isSubmitting}
                            inputProps={{ min: 1, step: 1 }}
                        />
                    </Grid>
                )}

                {/* Next Execution */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Next Execution Date"
                        helperText="Next day the schedule runs"
                        value={scheduleFormData.value.next_execution || ''}
                        onChange={e => scheduleFormData.value = {
                            ...scheduleFormData.value,
                            next_execution: e.target.value
                        }}
                        disabled={isSubmitting}
                        InputLabelProps={{
                            shrink: true
                        }}
                    />
                </Grid>

                {/* Execution time — the hour the client's catalog scraper is done */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="time"
                        label="Execution Time"
                        helperText="Office time (Europe/Madrid). Empty = 08:00"
                        value={scheduleFormData.value.execution_time || ''}
                        onChange={e => scheduleFormData.value = {
                            ...scheduleFormData.value,
                            execution_time: e.target.value
                        }}
                        disabled={isSubmitting}
                        InputLabelProps={{
                            shrink: true
                        }}
                    />
                </Grid>

                {/* Active Toggle */}
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={scheduleFormData.value.is_active}
                                onChange={e => scheduleFormData.value = {
                                    ...scheduleFormData.value,
                                    is_active: e.target.checked
                                }}
                                disabled={isSubmitting}
                            />
                        }
                        label="Active (schedule will run automatically)"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                        Batch Configuration
                    </Typography>
                </Grid>

                {/* Sites */}
                <Grid item xs={12}>
                    <SitesAutocomplete
                        disabled={isSubmitting}
                        required={false}
                    />
                </Grid>

                {/* Brands */}
                <Grid item xs={12}>
                    <BrandsAutocomplete
                        disabled={isSubmitting}
                    />
                </Grid>

                {/* Date Range - Only for raw2sku */}
                {batchType === 'raw2sku' && (
                    <>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                required
                                label="Date Range"
                                helperText="Relative date range calculated at execution time"
                                value={scheduleFormData.value.config.date_range_type || 'last_30_days'}
                                onChange={e => scheduleFormData.value = {
                                    ...scheduleFormData.value,
                                    config: {
                                        ...scheduleFormData.value.config,
                                        date_range_type: e.target.value
                                    }
                                }}
                                disabled={isSubmitting}
                            >
                                {DATE_RANGE_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {scheduleFormData.value.config.date_range_type === 'custom' && (
                            <>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="date"
                                        label="Date Start"
                                        value={createBatchFormData.value.dateStart || ''}
                                        onChange={e => {
                                            createBatchFormData.value = {
                                                ...createBatchFormData.value,
                                                dateStart: e.target.value
                                            }
                                            scheduleFormData.value = {
                                                ...scheduleFormData.value,
                                                config: {
                                                    ...scheduleFormData.value.config,
                                                    dateStart: e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="date"
                                        label="Date End"
                                        value={createBatchFormData.value.dateEnd || ''}
                                        onChange={e => {
                                            createBatchFormData.value = {
                                                ...createBatchFormData.value,
                                                dateEnd: e.target.value
                                            }
                                            scheduleFormData.value = {
                                                ...scheduleFormData.value,
                                                config: {
                                                    ...scheduleFormData.value.config,
                                                    dateEnd: e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Filter by
                                </Typography>
                                <ToggleButtonGroup
                                    value={createBatchFormData.value.dateField || 'end_date'}
                                    exclusive
                                    onChange={(e, newValue) => {
                                        if (newValue !== null) {
                                            createBatchFormData.value = {
                                                ...createBatchFormData.value,
                                                dateField: newValue
                                            }
                                        }
                                    }}
                                    disabled={isSubmitting}
                                    fullWidth
                                    size="small"
                                >
                                    <ToggleButton value="end_date">
                                        Last Seen Date
                                    </ToggleButton>
                                    <ToggleButton value="first_date">
                                        First Seen Date
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Grid>
                    </>
                )}

                <ScheduleExecutionFields disabled={isSubmitting} />

                {/* Advanced: Auto-Validation Thresholds */}
                <Grid item xs={12}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">
                                Advanced: Auto-Validation Thresholds
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        Leave empty to use system defaults. Controls automatic validation and exclusion during batch processing.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Min Rate Auto-validate"
                                        placeholder="Default: 0.98"
                                        value={scheduleFormData.value.config.autoValidation?.minRateAutovalidate ?? ''}
                                        onChange={(e) => scheduleFormData.value = {
                                            ...scheduleFormData.value,
                                            config: {
                                                ...scheduleFormData.value.config,
                                                autoValidation: {
                                                    ...scheduleFormData.value.config.autoValidation,
                                                    minRateAutovalidate: e.target.value === '' ? null : e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Minimum AI score to auto-validate (0–1). Default: 0.98">
                                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Min Gap 2nd Candidate"
                                        placeholder="Default: 0.05"
                                        value={scheduleFormData.value.config.autoValidation?.minGapSecondCandidate ?? ''}
                                        onChange={(e) => scheduleFormData.value = {
                                            ...scheduleFormData.value,
                                            config: {
                                                ...scheduleFormData.value.config,
                                                autoValidation: {
                                                    ...scheduleFormData.value.config.autoValidation,
                                                    minGapSecondCandidate: e.target.value === '' ? null : e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Min score gap between top and 2nd candidate. Default: 0.05">
                                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Max Rate Auto-exclude"
                                        placeholder="Default: 0.80 (0 = disable)"
                                        value={scheduleFormData.value.config.autoValidation?.maxRateAutoexclude ?? ''}
                                        onChange={(e) => scheduleFormData.value = {
                                            ...scheduleFormData.value,
                                            config: {
                                                ...scheduleFormData.value.config,
                                                autoValidation: {
                                                    ...scheduleFormData.value.config.autoValidation,
                                                    maxRateAutoexclude: e.target.value === '' ? null : e.target.value
                                                }
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Max score to auto-exclude. Set 0 to disable auto-exclusion. Default: 0.80">
                                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
        </DrawerSeeker>
    )
}
